import { Button, DatePicker, DropdownSelect } from '@components/common';
import type { DropdownSelectOption } from '@components/common/DropdownSelect';
import { Download } from 'lucide-react';
import React from 'react';
import type { InventoryStockLocationRecord } from '@/renderer/types/inventory';
import { INVENTORY_STOCK_MOVEMENT_TYPE_OPTIONS } from './stockMovementForm';

interface StockMovementsToolbarProps {
  title?: string;
  description?: string;
  dateFrom: string;
  dateTo: string;
  stockLocationFilter: string;
  movementTypeFilter: string;
  itemFilterIds: string[];
  locations: InventoryStockLocationRecord[];
  itemOptions: DropdownSelectOption[];
  itemOptionsLoading?: boolean;
  itemOptionsHasMore?: boolean;
  isExporting?: boolean;
  canExport?: boolean;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onStockLocationChange: (value: string) => void;
  onMovementTypeChange: (value: string) => void;
  onItemFilterChange: (value: string[]) => void;
  onItemSearchChange: (value: string) => void;
  onLoadMoreItems: () => void;
  onExportCsv: () => void;
}

export function StockMovementsToolbar({
  title = 'Stock movements',
  description = 'Review the item-by-item movement history that later purchasing, transfers, sales consumption, and adjustments will write into.',
  dateFrom,
  dateTo,
  stockLocationFilter,
  movementTypeFilter,
  itemFilterIds,
  locations,
  itemOptions,
  itemOptionsLoading = false,
  itemOptionsHasMore = false,
  isExporting = false,
  canExport = false,
  onDateFromChange,
  onDateToChange,
  onStockLocationChange,
  onMovementTypeChange,
  onItemFilterChange,
  onItemSearchChange,
  onLoadMoreItems,
  onExportCsv,
}: StockMovementsToolbarProps) {
  const itemDisplayLabel = itemFilterIds.length
    ? `${itemFilterIds.length} item${itemFilterIds.length === 1 ? '' : 's'} selected`
    : undefined;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
        <Button
          variant="outline"
          onClick={onExportCsv}
          isLoading={isExporting}
          disabled={!canExport}
          className="h-11 justify-center rounded-xl border border-emerald-200 bg-white px-4 text-sm text-emerald-700 hover:bg-emerald-50 hover:text-black lg:min-w-[140px]"
        >
          {!isExporting && <Download size={16} />}
          Export CSV
        </Button>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <DatePicker
          label="From"
          mode="single"
          value={dateFrom}
          onChange={onDateFromChange}
          placeholder="Select start date"
        />
        <DatePicker
          label="To"
          mode="single"
          value={dateTo}
          onChange={onDateToChange}
          placeholder="Select end date"
        />
        <DropdownSelect
          label="Item"
          values={itemFilterIds}
          options={itemOptions}
          placeholder="All moved items"
          customDisplayLabel={itemDisplayLabel}
          multiple
          searchable
          searchPlaceholder="Search moved items"
          onSearchChange={onItemSearchChange}
          hasMoreOptions={itemOptionsHasMore}
          isLoadingOptions={itemOptionsLoading}
          onLoadMoreOptions={onLoadMoreItems}
          emptyStateLabel="No moved items found."
          onMultiChange={onItemFilterChange}
        />
        <DropdownSelect
          label="Location"
          value={stockLocationFilter}
          options={[
            { value: 'all', label: 'All locations' },
            ...locations.map((location) => ({
              value: location.id,
              label: location.name,
            })),
          ]}
          onChange={onStockLocationChange}
        />
        <DropdownSelect
          label="Movement type"
          value={movementTypeFilter}
          options={[
            { value: 'all', label: 'All movement types' },
            ...INVENTORY_STOCK_MOVEMENT_TYPE_OPTIONS,
          ]}
          onChange={onMovementTypeChange}
        />
      </div>
    </div>
  );
}
